
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  LogOut, 
  User, 
  Calendar, 
  HelpCircle,
  FileText, 
  GraduationCap,
  BookOpen,
  Search
} from "lucide-react";
import { useAuth } from "@/lib/context/auth-context";
import { useToast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { NotificationDropdown } from "./NotificationDropdown";
import { useSidebar } from "@/components/ui/sidebar";
import MobileNav from "@/components/MobileNav";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ProfileCompletionIndicator } from "@/components/auth/ProfileCompletionIndicator";
import { getProfileCompletionStatus } from "@/lib/auth/utils/profile";

export function Header() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  const { state: sidebarState } = useSidebar();
  const { t } = useTranslation();
  
  const isActive = (path: string) => location.pathname.startsWith(path);
  const profileStatus = profile ? getProfileCompletionStatus(profile) : null;

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth/login");
    toast({
      title: "Logged out",
      description: "You have been successfully logged out",
    });
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full border-b bg-gradient-to-r from-[#004161] via-[#0f8f8c] to-[#004161] shadow-sm">
      <div className="grid h-16 grid-cols-3 items-center">
        {/* Left side - Logo only */}
        <div className="flex items-center justify-start">
          <div className="h-16 flex items-center pl-4">
            <img 
              src="/lovable-uploads/78ce5b8a-a3f5-4ace-b6b5-c26755a86e21.png"
              alt="NWSISD Logo"
              className="h-9 w-auto brightness-0 invert" // Added brightness-0 invert to make the logo white
              onClick={() => navigate("/")}
              style={{ cursor: "pointer" }}
            />
          </div>
        </div>
        
        {/* Center - Main Navigation Links */}
        <div className="flex justify-center">
          <nav className="hidden md:flex items-center space-x-3">
            <Button 
              variant="ghost" 
              size="sm" 
              className={cn(
                "transition-all duration-200 hover:bg-white/10 rounded-full px-4 gap-2 text-white",
                isActive("/courses") && "bg-white/20 text-white hover:bg-white/20"
              )}
              onClick={() => navigate("/courses")}
            >
              <GraduationCap className="h-4 w-4" />
              <span className="font-section">{t('header.courses')}</span>
            </Button>
            
            <Button 
              variant="ghost" 
              size="sm" 
              className={cn(
                "transition-all duration-200 hover:bg-white/10 rounded-full px-4 gap-2 text-white",
                isActive("/certificates") && "bg-white/20 text-white hover:bg-white/20"
              )}
              onClick={() => navigate("/certificates")}
            >
              <FileText className="h-4 w-4" />
              <span className="font-section">{t('header.certificates')}</span>
            </Button>
            
            <Button 
              variant="ghost" 
              size="sm"
              className={cn(
                "transition-all duration-200 hover:bg-white/10 rounded-full px-4 gap-2 text-white",
                isActive("/schedule") && "bg-white/20 text-white hover:bg-white/20"
              )}
              onClick={() => navigate("/schedule")}
            >
              <Calendar className="h-4 w-4" />
              <span className="font-section">{t('header.schedule')}</span>
            </Button>
          </nav>
        </div>

        {/* Right Side - Actions and Profile */}
        <div className="flex items-center justify-end gap-3 mr-4">
          {/* Search */}
          <div className="relative hidden sm:flex w-40 lg:w-64 transition-all duration-300 focus-within:w-72">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-white/70" />
            <Input 
              type="search" 
              placeholder={t('header.search')}
              className="pl-8 rounded-full border-white/20 bg-white/10 text-white placeholder:text-white/70 focus:bg-white/20 transition-all h-9"
            />
          </div>

          {/* Profile Completion Indicator */}
          {profileStatus && !profileStatus.isComplete && (
            <ProfileCompletionIndicator status={profileStatus} />
          )}

          {/* Help Button */}
          <Button 
            variant="ghost" 
            size="sm" 
            className={cn(
              "text-white hidden sm:flex rounded-full px-3 hover:bg-white/10 transition-all",
              isActive("/documentation") && "bg-white/20 hover:bg-white/20"
            )}
            onClick={() => navigate("/documentation")}
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            <span className="font-section">{t('header.help')}</span>
          </Button>

          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* Notifications */}
          <NotificationDropdown />
          
          {/* Mobile Navigation - Only visible on mobile */}
          <div className="md:hidden">
            <MobileNav />
          </div>
          
          {/* User Profile */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full hover:bg-white/10 transition-all duration-200 p-0">
                  <Avatar className="h-8 w-8 ring-2 ring-white/20">
                    <AvatarImage src="" alt={`${profile?.first_name || ''} ${profile?.last_name || ''}`} />
                    <AvatarFallback className="bg-white/20 text-white font-medium">
                      {profile?.first_name?.[0]}
                      {profile?.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-white" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-title font-medium leading-none">
                      {profile?.first_name} {profile?.last_name}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={() => navigate("/profile")}
                    className="cursor-pointer transition-colors font-section">
                    <User className="mr-2 h-4 w-4" />
                    <span>{t('header.profile')}</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}
                  className="cursor-pointer text-red-500 hover:text-red-600 transition-colors font-section">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t('header.logout')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
